import { useMemo } from 'react';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Text } from '@clay-run/glaze/text';
import type { NoteEntry } from '@shared/flow/notes';
import { FLOW_ICONS } from '../../icons';
import type { NotesPeopleFilter } from './flow-notes-store';
import { entryMatchesPeople } from './noteFormat';
import { NotesOptionRow, recordMark, type NotesOptionMark } from './NotesOptionRow';
import { NotesSectionedPicker } from './NotesSectionedPicker';
import { noNotesFooter, type SectionableOption } from './postingSections';

interface FilterOption extends SectionableOption {
  mark: NotesOptionMark;
  filter: NotesPeopleFilter;
  count: number;
}

/** What the trigger shows for the current filter. */
export function peopleFilterLabel(filter: NotesPeopleFilter, roster: { id: string; name: string }[]): string {
  if (filter === 'all') return 'All';
  if (filter === 'account') return 'Account';
  if (filter === 'contacts') return 'Contacts';
  return roster.find(r => r.id === filter.contactId)?.name ?? 'Contact';
}

const isSameFilter = (a: NotesPeopleFilter, b: NotesPeopleFilter) =>
  typeof a === 'string' || typeof b === 'string'
    ? a === b
    : a.contactId === b.contactId;

export interface PeopleFilterProps {
  /** The loaded thread entries; counts come from the same predicate that filters the list. */
  entries: NoteEntry[];
  roster: { id: string; name: string }[];
  value: NotesPeopleFilter;
  onChange: (filter: NotesPeopleFilter) => void;
}

/**
 * The account thread's People VIEW filter (what you see; "Posting to" decides
 * where a note goes). A searchable popover: All, Account, Contacts, then each
 * contact, each with its note count. A reset button shows when not All.
 */
export function PeopleFilter({ entries, roster, value, onChange }: PeopleFilterProps) {
  const options = useMemo<FilterOption[]>(() => {
    // Counts are taken against an account thread, the only place this filter shows.
    const accountThread = { kind: 'account' } as const;
    const countFor = (filter: NotesPeopleFilter) => entries.filter(e => entryMatchesPeople(e, accountThread, filter)).length;
    const contacts = roster
      .map((r): FilterOption => {
        const count = countFor({ contactId: r.id });
        return {
          key: `c:${r.id}`,
          label: r.name,
          section: 'people',
          mark: recordMark('contact', r.id),
          filter: { contactId: r.id },
          count,
          // People with no notes are found by searching, but the current selection always stays listed.
          hiddenUntilSearch: count === 0 && !(typeof value === 'object' && value.contactId === r.id),
        };
      })
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return [
      { key: 'all', label: 'All', section: 'account', mark: { kind: 'group', icon: 'notes' }, filter: 'all', count: countFor('all') },
      { key: 'account', label: 'Account', section: 'account', mark: { kind: 'group', icon: 'companies' }, filter: 'account', count: countFor('account') },
      { key: 'contacts', label: 'Contacts', section: 'people', mark: { kind: 'group', icon: 'people' }, filter: 'contacts', count: countFor('contacts') },
      ...contacts,
    ];
  }, [entries, roster, value]);

  const selected = options.find(o => isSameFilter(o.filter, value)) ?? null;
  const label = peopleFilterLabel(value, roster);

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <NotesSectionedPicker
        options={options}
        selected={selected}
        onSelect={o => onChange(o.filter)}
        aria-label={`People filter: ${label}`}
        placeholder={label}
        searchPlaceholder="Search people"
        popupWidth="full"
        className="max-w-44"
        renderValue={o => <span className="truncate">{o.label}</span>}
        renderRow={o => (
          <NotesOptionRow
            label={o.label}
            mark={o.mark}
            trailing={
              <Text size="xs" variant="tertiary" className="shrink-0 tabular-nums" data-testid="people-filter-count">
                {o.count}
              </Text>
            }
          />
        )}
        footer={hidden =>
          hidden > 0 && (
            <Text size="xs" variant="tertiary" className="block px-2.5 py-1.5">
              {noNotesFooter(hidden)}
            </Text>
          )
        }
      />
      {value !== 'all' && (
        <IconButton variant="ghost" size="xs" icon={FLOW_ICONS.clear} aria-label="Reset people filter" onClick={() => onChange('all')} />
      )}
    </div>
  );
}
